import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Navigate, Outlet, useLocation, useMatches } from "react-router-dom";
import { AppShell } from "@/components/app-shell";
import Loader from "@/components/loader";
import { PostHogIdentitySync } from "@/components/posthog-identity-sync";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WorkerWebAccessGate } from "@/components/worker-web-access-gate";
import {
  canAccessOnboardingInDevelopment,
  getOnboardingRedirectPath,
  toOnboardingStatus,
} from "@/lib/onboarding";
import { UserProvider, useOptionalUser } from "@/lib/user-context";
import { queryClient, trpc } from "./utils/trpc";

export default function App() {
  const matches = useMatches();
  const isFullScreenRoute = matches.some((match) => {
    const handle = match.handle as { isFullScreen?: boolean } | undefined;
    return handle?.isFullScreen;
  });
  const allowFullScreenScroll = matches.some((match) => {
    const handle = match.handle as { allowScroll?: boolean } | undefined;
    return handle?.allowScroll;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          disableTransitionOnChange
          storageKey="vite-ui-theme"
        >
          <TooltipProvider>
            <PostHogIdentitySync />
            <AppLayout
              isFullScreenRoute={isFullScreenRoute}
              allowFullScreenScroll={allowFullScreenScroll}
            />
            <Toaster richColors />
          </TooltipProvider>
        </ThemeProvider>
      </UserProvider>
      <ReactQueryDevtools position="bottom" buttonPosition="bottom-right" />
    </QueryClientProvider>
  );
}

function AppLayout({
  isFullScreenRoute,
  allowFullScreenScroll,
}: {
  isFullScreenRoute: boolean;
  allowFullScreenScroll: boolean;
}) {
  const location = useLocation();
  const user = useOptionalUser();
  const allowDevelopmentOnboardingAccess = canAccessOnboardingInDevelopment({
    pathname: location.pathname,
    isDevelopment: import.meta.env.DEV,
    systemRole: user?.systemRole ?? null,
  });
  const authenticatedUserId = user?.session?.user?.id ?? null;
  const isAwaitingAccessResolution =
    Boolean(authenticatedUserId) &&
    (user?.isSessionLoading || user?.isSystemRoleLoading || !user?.systemRole);
  const shouldResolveOnboardingStatus =
    Boolean(authenticatedUserId) && !isAwaitingAccessResolution;
  const userProfileOptions = trpc.getUserProfile.queryOptions();
  // Fetch in parallel with systemRole; only act on data once both are ready
  const userProfile = useQuery({
    ...userProfileOptions,
    enabled:
      Boolean(authenticatedUserId) && (userProfileOptions.enabled ?? true),
  });
  const containerClassName = isFullScreenRoute
    ? allowFullScreenScroll
      ? "relative h-svh w-full overflow-y-auto overflow-x-hidden"
      : "relative h-svh w-full overflow-hidden"
    : "relative min-h-svh w-full";

  if (
    authenticatedUserId &&
    user?.systemRole === "worker" &&
    !allowDevelopmentOnboardingAccess
  ) {
    return <WorkerWebAccessGate />;
  }

  if (isAwaitingAccessResolution) {
    return <Loader className="absolute inset-0 h-full" />;
  }

  if (shouldResolveOnboardingStatus && userProfile.isLoading) {
    return <Loader className="absolute inset-0 h-full" />;
  }

  const onboardingRedirectPath =
    shouldResolveOnboardingStatus && userProfile.data
      ? getOnboardingRedirectPath({
          pathname: location.pathname,
          onboardingStatus: toOnboardingStatus(
            userProfile.data.onboardingStatus
          ),
          systemRole: user?.systemRole ?? null,
          allowDevelopmentAccess: allowDevelopmentOnboardingAccess,
        })
      : null;

  if (onboardingRedirectPath) {
    return <Navigate to={onboardingRedirectPath} replace />;
  }

  const hasAuthenticatedShell =
    !isFullScreenRoute && Boolean(authenticatedUserId);

  return (
    <div className={containerClassName}>
      {hasAuthenticatedShell ? (
        <AppShell>
          <Outlet />
        </AppShell>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
